import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

export interface NewCustomerData {
  name: string;
  mobile?: string;
}

interface NewCustomerDialogProps {
  isOpen: boolean;
  email: string;
  onClose: () => void;
  onSubmit: (data: NewCustomerData) => void;
  isLoading?: boolean;
}

/**
 * 새 고객 등록 다이얼로그
 * - 기존에 없는 이메일로 고객을 추가할 때 이름(필수)과 전화번호(선택)를 입력받습니다.
 */
const NewCustomerDialog: React.FC<NewCustomerDialogProps> = ({
  isOpen,
  email,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");

  // 다이얼로그가 열릴 때 폼 초기화 (이메일의 @ 앞 부분을 기본 이름으로)
  useEffect(() => {
    if (isOpen) {
      setName(email.split("@")[0] || "");
      setMobile("");
    }
  }, [isOpen, email]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    onSubmit({
      name: name.trim(),
      mobile: mobile.trim() || undefined,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Dialog */}
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              새 고객 등록
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              disabled={isLoading}
            >
              <X size={20} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Email (읽기 전용) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                이메일
              </label>
              <input
                type="email"
                value={email}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
              />
            </div>

            {/* Name (필수) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                이름 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="고객 이름을 입력하세요"
                required
                autoFocus
              />
            </div>

            {/* Mobile (선택) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                전화번호
              </label>
              <input
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="010-0000-0000"
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={isLoading}
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isLoading || !name.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-[#222] rounded-lg hover:bg-[#333] disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isLoading ? "추가 중..." : "추가"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewCustomerDialog;
